import { UrlQueryFilter } from "../../shared/hooks/useUrlQuery";
import { createRouteHelpers } from "../../shared/utils/createRouteHelpers";

// Internal path segments — used only to compose the helpers below.
// Consumers must import the `routeHelpers*` objects, never a raw path string.
const PATH_WORK_LOGS = "/member/work-logs";
const PATH_WORK_LOGS_PAYEE = `${PATH_WORK_LOGS}/payee` as const;
const PATH_WORK_LOGS_PAYER = `${PATH_WORK_LOGS}/payer` as const;
const PARAMETER_WORK_LOG_ID = "workLogId";

// Deep-link search carried into the payer list — a filter preset applied to
// the table when arriving from another screen (e.g. "View all").
type PayerListSearch = { filter?: UrlQueryFilter };

// Deep-link search carried into the payee in-progress tab — either a filter
// preset ("view existing work logs"), or a payer/engagement pair that
// auto-opens the create-work-log modal on arrival.
type PayeeInProgressSearch = {
  filter?: UrlQueryFilter;
  payerId?: string;
  engagementId?: string;
};

// Root
export const routeHelpersWorkLogs = createRouteHelpers(PATH_WORK_LOGS);

export const routeHelpersWorkLogEdit = createRouteHelpers(
  `${PATH_WORK_LOGS}/edit/:${PARAMETER_WORK_LOG_ID}` as const
);

// Legacy role-agnostic details — kept for backward compatibility with
// notifications and emails sent before role-scoped URLs were introduced.
// Rendered by RouteWorkLogRedirect, which forwards to the payer/payee URL.
export const routeHelpersWorkLogLegacyDetails = createRouteHelpers(
  `${PATH_WORK_LOGS}/:${PARAMETER_WORK_LOG_ID}` as const
);

// Payee
export const routeHelpersWorkLogsPayee =
  createRouteHelpers(PATH_WORK_LOGS_PAYEE);

export const routeHelpersWorkLogPayeeDetails = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYEE}/:${PARAMETER_WORK_LOG_ID}` as const
);

export const routeHelpersWorkLogsPayeeInProgress = createRouteHelpers<
  never,
  { search: PayeeInProgressSearch }
>(`${PATH_WORK_LOGS_PAYEE}/in-progress` as const);

export const routeHelpersWorkLogsPayeeSubmitted = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYEE}/submitted` as const
);

export const routeHelpersWorkLogsPayeeApproved = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYEE}/approved` as const
);

export const routeHelpersWorkLogsPayeeRejected = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYEE}/rejected` as const
);

// Payer
export const routeHelpersWorkLogsPayer = createRouteHelpers<
  never,
  { search: PayerListSearch }
>(PATH_WORK_LOGS_PAYER);

export const routeHelpersWorkLogPayerDetails = createRouteHelpers<
  { workLogId: string },
  { search: { bulkReview: string } }
>(`${PATH_WORK_LOGS_PAYER}/:${PARAMETER_WORK_LOG_ID}` as const);

export const routeHelpersWorkLogsPayerInProgress = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYER}/in-progress` as const
);

export const routeHelpersWorkLogsPayerSubmitted = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYER}/submitted` as const
);

export const routeHelpersWorkLogsPayerDashboard = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYER}/dashboard` as const
);

export const routeHelpersWorkLogsPayerToApprove = createRouteHelpers<
  never,
  { search: PayerListSearch }
>(`${PATH_WORK_LOGS_PAYER}/to-approve` as const);

export const routeHelpersWorkLogsPayerApproved = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYER}/approved` as const
);

export const routeHelpersWorkLogsPayerRejected = createRouteHelpers(
  `${PATH_WORK_LOGS_PAYER}/rejected` as const
);
